import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createTaskRelation } from '@/lib/api/generated/api';
import type {
  CreateTaskRelationRequest,
  DirectTaskRelationsResponse,
  TaskRelationView,
} from '@/lib/api/generated/model';
import { mapTaskRelationError } from '../errors';
import { taskRelationsKeys } from '../queryKeys';

/**
 * Wraps generated `createTaskRelation`, throwing the person-facing message
 * (research D5) rather than the raw domain error on any non-201 response —
 * both widget modes (staged and persisted) consume this directly.
 */
export function useAddTaskRelation(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: CreateTaskRelationRequest,
    ): Promise<TaskRelationView> => {
      const response = await createTaskRelation(workspaceId, request);
      if (response.status !== 201) {
        throw new Error(mapTaskRelationError(response.data));
      }
      return response.data;
    },
    onSuccess: (relation) => {
      // Only the flat `.direct` cache can be patched correctly here — the
      // `.related` response is a graph traversal (depth, relationship,
      // workflow grouping) this hook has no way to recompute, so it is left
      // to its own next refetch (FR-015 only requires "by next open").
      for (const taskId of [relation.parent_task_id, relation.child_task_id]) {
        queryClient.setQueryData(
          taskRelationsKeys.direct(workspaceId, taskId),
          (prev: DirectTaskRelationsResponse | undefined) =>
            prev ? { ...prev, relations: [...prev.relations, relation] } : prev,
        );
        // See useRemoveTaskRelation: the global QueryClient's default
        // staleTime (60s) means the `.related` dialog's `enabled: open`
        // reopen won't refetch on its own within that window without this.
        void queryClient.invalidateQueries({
          queryKey: taskRelationsKeys.related(workspaceId, taskId),
        });
      }
    },
  });
}
